import React, { useEffect, useState } from "react";
import {
  AutocompleteInput,
  EditProps,
  ReferenceInput,
  required,
  SimpleForm,
  useDataProvider,
  useEditController,
} from "react-admin";
import { GetParam } from "utils/tools";
import { Grid, Typography } from "@material-ui/core";
import CustomEditToolbar from "../../components/CustomEditToolbar";
import SanitizedGrid from "../../components/SanitizedGrid";
import SubcontractorErpDatagrid from "./components/SubcontractorErpDatagrid";
import CustomMonthPickerInput from "../../components/CustomMonthPickerInput";
import EditWrapper from "../../components/EditWrapper";
import PermissionLoading from "../../components/PermissionLoading";

const SubcontractorErpEdit = (props: EditProps) => {
  const { id } = props;
  const param = GetParam();
  const provider = useDataProvider();
  const [disableForm, setDisableForm] = useState<boolean>(false);

  const { record, loading } = useEditController(props);

  const breadcrumb = [
    {
      title: "Subcontractors",
      link: `/subcontractors/${param.get("subcontractorId")}/show`,
    },
    {
      title: "Financial",
      link: `/subcontractors/${param.get("subcontractorId")}/show/2`,
    },
    {
      title: id,
      link: "",
    },
  ];

  useEffect(() => {
    provider.getOne("subcontractorErps", { id: id }).then((result) => {
      setDisableForm(result.data.isInvoiced);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatRecord = (res: any) => {
    if (res) {
      // Safely handle and convert month and year to subcontractorErpPeriod
      if (typeof res.month === "number" && typeof res.year === "number") {
        // Ensure month is between 1 and 12
        const month =
          res.month >= 1 && res.month <= 12
            ? String(res.month).padStart(2, "0")
            : "01"; // Default to January if out of range

        // Ensure year is reasonable (e.g., a four-digit year)
        const year =
          res.year > 1000 && res.year < 3000
            ? String(res.year)
            : new Date().getFullYear().toString(); // Default to the current year if invalid

        // Combine month and year into the desired format
        res.subcontractorErpPeriod = `${month}/${year}`;
      }

      return res;
    }
  };

  const transform = (data: any) => {
    data.subcontractorErpItems = [...data.itemsToSend];

    delete data.itemsToSend;
    delete data.subcontractorErpPeriod;

    return data;
  };

  if (loading) {
    return <PermissionLoading />;
  }

  return (
    <>
      <EditWrapper
        breadcrumb={breadcrumb}
        actions={null}
        transform={transform}
        isDefaultTitle
        {...props}
      >
        <SimpleForm
          record={formatRecord(record)}
          toolbar={
            <CustomEditToolbar
              redirectLink={`/subcontractors/${param.get(
                "subcontractorId",
              )}/show/2`}
              isShowDelete={false}
            />
          }
          name="manageSubcontractorErp"
        >
          <SanitizedGrid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6">General Info</Typography>
            </Grid>

            <Grid item xs={4}>
              <ReferenceInput
                label="Subcontractor"
                source="subcontractorId"
                reference="subcontractors"
                validate={required()}
                disabled
              >
                <AutocompleteInput optionText="name" />
              </ReferenceInput>
            </Grid>
            <Grid item xs={8}></Grid>

            <Grid item xs={4}>
              <ReferenceInput
                label="School"
                source="schoolId"
                reference="schools"
                validate={required()}
                disabled
              >
                <AutocompleteInput optionText="name" />
              </ReferenceInput>
            </Grid>
            <Grid item xs={8}></Grid>

            <Grid item xs={4}>
              <CustomMonthPickerInput
                name="subcontractorErpPeriod"
                label="ERP Period"
                disableValue={true}
              />
            </Grid>
            <Grid item xs={8}></Grid>

            <Grid item xs={12}>
              <Typography variant="h6">ERP Charge Table</Typography>
            </Grid>

            <Grid item xs={8}>
              <SubcontractorErpDatagrid isDisabled={disableForm} />
            </Grid>
          </SanitizedGrid>
        </SimpleForm>
      </EditWrapper>
    </>
  );
};

export default SubcontractorErpEdit;
